import { useSuiClient } from '@mysten/dapp-kit';
import type { MoveCallMetrics } from '@mysten/sui.js/client';
import { useQuery } from '@tanstack/react-query';

import { getCpcPackageHints } from '~/config/cpcPackages.config';
import { useEnhancedRpcClient } from '~/hooks/useEnhancedRpc';
import {
	buildMoveCallMetricsFromTransactions,
	hasMoveCallMetrics,
	mergeMoveCallMetrics,
} from '~/utils/cpcMoveCallMetrics';

const SCAN_PAGE_SIZE = 50;
const SCAN_MAX_PAGES = 12;
const RANK_LIMIT = 20;
const REFETCH_MS = 60_000;

async function scanRecentMoveCallMetrics(
	client: ReturnType<typeof useSuiClient>,
): Promise<MoveCallMetrics> {
	const txs = [];
	let cursor: string | null = null;
	for (let page = 0; page < SCAN_MAX_PAGES; page += 1) {
		const res = await client.queryTransactionBlocks({
			cursor,
			order: 'descending',
			limit: SCAN_PAGE_SIZE,
			options: { showInput: true },
		});
		txs.push(...res.data);
		if (!res.hasNextPage || !res.nextCursor) break;
		cursor = res.nextCursor;
	}

	const metrics = buildMoveCallMetricsFromTransactions(txs, RANK_LIMIT);
	const seeds = getCpcPackageHints().map((h) => ({
		package: h.package,
		module: h.module,
		function: h.function,
	}));

	return {
		rank3Days: mergeMoveCallMetrics(metrics.rank3Days, seeds, RANK_LIMIT),
		rank7Days: mergeMoveCallMetrics(metrics.rank7Days, seeds, RANK_LIMIT),
		rank30Days: mergeMoveCallMetrics(metrics.rank30Days, seeds, RANK_LIMIT),
	};
}

export function useCpcMoveCallMetrics() {
	const rpc = useEnhancedRpcClient();
	const client = useSuiClient();

	return useQuery({
		queryKey: ['cpc-move-call-metrics'],
		queryFn: async (): Promise<MoveCallMetrics> => {
			try {
				const indexed = await rpc.getMoveCallMetrics();
				if (hasMoveCallMetrics(indexed)) return indexed;
			} catch {
				// LOCAL fullnode often lacks suix_getMoveCallMetrics data.
			}
			return scanRecentMoveCallMetrics(client);
		},
		refetchInterval: REFETCH_MS,
		staleTime: 30_000,
	});
}
